import type { BreadcrumbTitleRegistration } from "./breadcrumbs-store";

/** One crumb of the trail above a page. */
export interface Breadcrumb {
	/** The path up to and including this crumb's segment, which is where the crumb leads. */
	path: string;
	/** The name a page registered for the segment, or the segment itself. */
	label: string;
	/** Whether `label` is a registered name rather than the URL's own word. */
	isNamed: boolean;
	/** Whether the crumb leads anywhere. */
	isLink: boolean;
}

/**
 * Turn a pathname into its breadcrumb trail, one crumb per segment.
 *
 * A crumb links only where a page is known to exist. A named segment is an entity a mounted page
 * named, so its path is that page's. A static segment links where the navigation links, because
 * segments like `stock`, `data` or `suborders` have no page and would open a blank screen — and
 * while no navigation has published its paths, every static crumb links. The last crumb is the
 * page already open and never links.
 *
 * @param pathname - The current location's pathname.
 * @param registrations - The names mounted components gave URL segments.
 * @param pagePaths - The paths the navigation links to, or `null` when none were published.
 * @returns The crumbs in URL order; an empty segment, as a trailing slash leaves, has none.
 */
export function getBreadcrumbs(
	pathname: string,
	registrations: Record<string, BreadcrumbTitleRegistration>,
	pagePaths: ReadonlySet<string> | null,
) {
	const titles = getTitlesBySegment(registrations);
	const segments = getPathSegments(pathname);
	const breadcrumbs: Array<Breadcrumb> = [];
	for (const [index, segment] of segments.entries()) {
		const path = `/${segments.slice(0, index + 1).join("/")}`;
		const title = titles.get(segment) ?? null;
		const isCurrentPage = index === segments.length - 1;
		const isKnownPage = title !== null || pagePaths === null || pagePaths.has(path);
		breadcrumbs.push({
			path,
			label: title ?? segment,
			isNamed: title !== null,
			isLink: !isCurrentPage && isKnownPage,
		});
	}
	return breadcrumbs;
}

/** The registered name of every segment; of two names for one segment, the later one wins. */
function getTitlesBySegment(registrations: Record<string, BreadcrumbTitleRegistration>) {
	const titles = new Map<string, string>();
	for (const registration of Object.values(registrations)) {
		titles.set(registration.segment, registration.title);
	}
	return titles;
}

function getPathSegments(pathname: string) {
	const segments: Array<string> = [];
	for (const segment of pathname.split("/")) {
		if (segment) segments.push(segment);
	}
	return segments;
}
